import React, { useEffect, useState, useContext, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import 'intersection-observer';

import { WindowContext } from '../../providers';

import { useRouter } from '../../hooks';
import { Poster, Section, Video, Wrapper } from './styles';
import { Storyblok, StoryblokSection } from '../../storyblok';

const isAutoplaySupported = (callback: unknown) => {
    if (typeof callback !== 'function') return false;

    const video = document.createElement('video');
    video.pause();
    const player = 'play' in video && video.play();

    callback(
        !video.paused || ('Promise' in window && player instanceof Promise),
    );
};

export const VideoSection = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.VideoSection>) => {
    const {
        component,
        autoplay,
        video_poster,
        video_src_fullhd,
        video_src_mobile,
        video_src_sd,
        redirect_on_click,
    } = sectionData;
    const [currentVideoSrc, setCurrentVideoSrc] = useState<string>('');
    const { navigate } = useRouter();

    const [ref, inView] = useInView({});
    const videoRef = useRef<HTMLVideoElement>(null);
    const { currentMediaName } = useContext(WindowContext);

    useEffect(() => {
        switch (currentMediaName) {
            case 'desktop':
                setCurrentVideoSrc(video_src_fullhd);
                return;
            case 'mobile':
                setCurrentVideoSrc(video_src_sd);
                return;
            default:
                setCurrentVideoSrc(video_src_mobile);
        }
    }, [currentMediaName]);

    useEffect(() => {
        if (!currentVideoSrc || !videoRef.current) return;

        videoRef.current.setAttribute('muted', '1');
        videoRef.current.setAttribute('playsinline', '1');
        videoRef.current.setAttribute('autoplay', '1');

        if (inView && currentVideoSrc) {
            if (autoplay && isAutoplaySupported && videoRef) {
                videoRef.current.play();
            }
        } else {
            videoRef.current.pause();
        }
    }, [currentVideoSrc, inView, autoplay]);

    if (!currentVideoSrc) {
        return null;
    }

    const handleVideoClick = () => {
        if (!redirect_on_click) return;

        navigate(redirect_on_click);
    };

    return (
        <Section
            ref={ref}
            onClick={() => handleVideoClick()}
            data-block={component}
        >
            <Wrapper asLink={redirect_on_click}>
                <Poster image={video_poster} className="VideoSection__poster" />
                {currentVideoSrc && (
                    <Video muted loop autoPlay={autoplay} ref={videoRef}>
                        <source src={currentVideoSrc} type="video/mp4" />
                        Your browser does not support HTML5 video.
                    </Video>
                )}
            </Wrapper>
        </Section>
    );
};
